import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { RootState } from '@/store';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  PersonAdd,
  Edit,
  Delete,
  Send,
  KeyboardArrowDown,
  Cancel as CancelIcon,
  PersonRemove as PersonRemoveIcon,
  MailOutline as MailOutlineIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  SwapHoriz as TransferIcon,
} from '@mui/icons-material';
import { userManagementService, OrganizationMember, PendingInvitation } from '@/services/userManagementService';
import { OrganizationRole } from '@/types/enums';

const UserManagement: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<PendingInvitation[]>([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmRemoveDialogOpen, setConfirmRemoveDialogOpen] = useState(false);
  const [confirmCancelDialogOpen, setConfirmCancelDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<OrganizationMember | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<PendingInvitation | null>(null);
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [expandedInvitationId, setExpandedInvitationId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'Organization_Admin' | 'Organization_Member'>('Organization_Member');
  const [editRole, setEditRole] = useState<'Organization_Admin' | 'Organization_Member'>('Organization_Member');
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferMember, setTransferMember] = useState<OrganizationMember | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.currentOrganizationId) {
      loadMembers();
      loadInvitations();
    }
  }, [user?.currentOrganizationId]);

  const loadMembers = async () => {
    if (!user?.currentOrganizationId) return;
    const currentOrg = user.availableOrganizations?.find(
      (org: any) => org.organizationId === user.currentOrganizationId
    );
    if (!currentOrg?.organizationSlug) return;
    
    try {
      const data = await userManagementService.getOrganizationMembers(currentOrg.organizationSlug);
      setMembers(data || []);
    } catch (err) {
      console.error('Failed to load members:', err);
      setMembers([]);
      toast.error('Failed to load organization members');
    }
  };

  const loadInvitations = async () => {
    if (!user?.currentOrganizationId) return;
    const currentOrg = user.availableOrganizations?.find(
      (org: any) => org.organizationId === user.currentOrganizationId
    );
    if (!currentOrg?.organizationSlug) return;
    
    try {
      const data = await userManagementService.getPendingInvitations(currentOrg.organizationSlug);
      setInvitations(data || []);
    } catch (err) {
      console.error('Failed to load invitations:', err);
      setInvitations([]);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!user?.currentOrganizationId) return;
    const currentOrg = user.availableOrganizations?.find(
      (org: any) => org.organizationId === user.currentOrganizationId
    );
    if (!currentOrg?.organizationSlug) return;

    setLoading(true);
        
    try {
      await userManagementService.inviteUser(currentOrg.organizationSlug, {
        email: inviteEmail,
        role: inviteRole,
      });
      
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole('Organization_Member');
      setInviteDialogOpen(false);
      await loadInvitations();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = async () => {
    if (!selectedMember || !user?.currentOrganizationId) return;
    const currentOrg = user.availableOrganizations?.find(
      (org: any) => org.organizationId === user.currentOrganizationId
    );
    if (!currentOrg?.organizationSlug) return;

    setLoading(true);
        
    try {
      await userManagementService.updateMemberRole(currentOrg.organizationSlug, selectedMember.id, {
        role: editRole,
      });
      
      toast.success(`Role updated for ${selectedMember.email}`);
      setEditDialogOpen(false);
      setSelectedMember(null);
      await loadMembers();
      
      // Note: If the current user's role was updated, their token will be invalidated
      // and they will be automatically logged out by the API interceptor
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (member: OrganizationMember) => {
    if (member.role === 'Organization_Owner') {
      toast.error('Cannot remove organization owner');
      return;
    }

    if (member.id === user?.id) {
      toast.error('Cannot remove yourself');
      return;
    }

    setSelectedMember(member);
    setDeleteConfirmEmail(''); // Reset confirmation email
    setConfirmRemoveDialogOpen(true);
  };

  const confirmRemoveMember = async () => {
    if (!selectedMember || !user?.currentOrganizationId) return;
    
    // Validate email confirmation
    if (deleteConfirmEmail !== selectedMember.email) {
      toast.error('Email confirmation does not match');
      return;
    }
    
    const currentOrg = user.availableOrganizations?.find(
      (org: any) => org.organizationId === user.currentOrganizationId
    );
    if (!currentOrg?.organizationSlug) return;

    setLoading(true);
        
    try {
      await userManagementService.removeMember(currentOrg.organizationSlug, selectedMember.id);
      
      toast.success(`${selectedMember.email} has been removed`);
      setConfirmRemoveDialogOpen(false);
      setSelectedMember(null);
      setDeleteConfirmEmail('');
      await loadMembers();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitation: PendingInvitation) => {
    setSelectedInvitation(invitation);
    setConfirmCancelDialogOpen(true);
  };

  const confirmCancelInvitation = async () => {
    if (!selectedInvitation || !user?.currentOrganizationId) return;
    const currentOrg = user.availableOrganizations?.find(
      (org: any) => org.organizationId === user.currentOrganizationId
    );
    if (!currentOrg?.organizationSlug) return;

    setLoading(true);
        
    try {
      await userManagementService.cancelInvitation(currentOrg.organizationSlug, selectedInvitation.id);
      
      toast.success(`Invitation for ${selectedInvitation.email} has been canceled`);
      setConfirmCancelDialogOpen(false);
      setSelectedInvitation(null);
      await loadInvitations();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to cancel invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invitation: PendingInvitation) => {
    if (!user?.currentOrganizationId) return;
    const currentOrg = user.availableOrganizations?.find(
      (org: any) => org.organizationId === user.currentOrganizationId
    );
    if (!currentOrg?.organizationSlug) return;
    
    setLoading(true);
        
    try {
      await userManagementService.resendInvitation(currentOrg.organizationSlug, invitation.id);
      
      toast.success(`Invitation resent to ${invitation.email}`);
      await loadInvitations();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to resend invitation');
    } finally {
      setLoading(false);
    }
  };

  const currentOrg = user?.availableOrganizations?.find(
    (org: any) => org.organizationId === user?.currentOrganizationId
  );
  const isOwner = currentOrg?.role === OrganizationRole.OWNER;

  const handleTransferOwnership = async () => {
    if (!transferMember || !currentOrg?.organizationSlug) return;

    setLoading(true);
    try {
      await userManagementService.transferOwnership(currentOrg.organizationSlug, transferMember.id);
      toast.success(`Ownership transferred to ${transferMember.email}. You are now an Admin.`);
      setTransferDialogOpen(false);
      setTransferMember(null);
      await loadMembers();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Failed to transfer ownership');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Organization_Owner': return 'primary'; // Blue instead of red
      case 'Organization_Admin': return 'secondary'; // Purple instead of orange
      case 'Organization_Member': return 'info';
      default: return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'Organization_Owner': return 'Owner';
      case 'Organization_Admin': return 'Admin';
      case 'Organization_Member': return 'Member';
      default: return role;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'suspended': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    // Convert to sentence case (e.g., ACTIVE -> Active, PENDING -> Pending)
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_/g, ' ');
  };

  return (
    <Box>
      {/* Members Section */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Members ({members.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite User
          </Button>
        </Box>

        {/* Desktop Table View */}
        <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' }, overflowX: "auto", width: "100%" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell width={40}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <React.Fragment key={member.id}>
                    <TableRow
                      hover
                      onClick={() => setExpandedMemberId(expandedMemberId === member.id ? null : member.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={getRoleLabel(member.role)}
                          color={getRoleColor(member.role) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(member.status)}
                          color={getStatusColor(member.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(member.joinedAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell sx={{ py: 0, px: 1 }}>
                        <KeyboardArrowDown
                          sx={{
                            transform: expandedMemberId === member.id ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                            color: 'action.active',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    {expandedMemberId === member.id && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ bgcolor: 'grey.50', py: 2 }}>
                          <Box display="flex" gap={2}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Edit />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedMember(member);
                                setEditRole(member.role === 'Organization_Owner' ? 'Organization_Admin' : member.role);
                                setEditDialogOpen(true);
                              }}
                              disabled={member.role === 'Organization_Owner' || loading}
                            >
                              Edit Role
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              color="error"
                              startIcon={<Delete />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveMember(member);
                              }}
                              disabled={member.role === 'Organization_Owner' || member.id === user?.id || loading}
                            >
                              Remove Member
                            </Button>
                            {isOwner && member.id !== user?.id && member.role !== 'Organization_Owner' && (
                              <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                startIcon={<TransferIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTransferMember(member);
                                  setTransferDialogOpen(true);
                                }}
                                disabled={loading}
                              >
                                Transfer Ownership
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

        {/* Mobile Card View */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {members.map((member) => (
            <Card key={member.id} sx={{ mb: 2 }} variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="body1" fontWeight="medium">
                      {member.email}
                    </Typography>
                  </Box>
                  <Chip
                    label={getStatusLabel(member.status)}
                    size="small"
                    color={getStatusColor(member.status) as any}
                  />
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Role
                    </Typography>
                    <Box>
                      <Chip
                        label={getRoleLabel(member.role)}
                        color={getRoleColor(member.role) as any}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Joined
                    </Typography>
                    <Typography variant="body2">
                      {new Date(member.joinedAt).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>

                <Box display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => {
                      setSelectedMember(member);
                      setEditRole(member.role === 'Organization_Owner' ? 'Organization_Admin' : member.role);
                      setEditDialogOpen(true);
                    }}
                    disabled={member.role === 'Organization_Owner' || loading}
                    fullWidth
                  >
                    Edit Role
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => handleRemoveMember(member)}
                    disabled={member.role === 'Organization_Owner' || member.id === user?.id || loading}
                    fullWidth
                  >
                    Remove Member
                  </Button>
                  {isOwner && member.id !== user?.id && member.role !== 'Organization_Owner' && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="warning"
                      startIcon={<TransferIcon />}
                      onClick={() => {
                        setTransferMember(member);
                        setTransferDialogOpen(true);
                      }}
                      disabled={loading}
                      fullWidth
                    >
                      Transfer Ownership
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Pending Invitations ({invitations.length})
          </Typography>

          {/* Desktop Table View */}
          <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' }, overflowX: "auto", width: "100%" }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Invited</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell width={40}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((invitation) => (
                    <React.Fragment key={invitation.id}>
                      <TableRow
                        hover
                        onClick={() => setExpandedInvitationId(expandedInvitationId === invitation.id ? null : invitation.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>{invitation.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={getRoleLabel(invitation.role)}
                            color={getRoleColor(invitation.role) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(invitation.invitedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(invitation.expiresAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ py: 0, px: 1 }}>
                          <KeyboardArrowDown
                            sx={{
                              transform: expandedInvitationId === invitation.id ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s',
                              color: 'action.active',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                      {expandedInvitationId === invitation.id && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ bgcolor: 'grey.50', py: 2 }}>
                            <Box display="flex" gap={2}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Send />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResendInvitation(invitation);
                                }}
                                disabled={loading}
                              >
                                Resend
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                startIcon={<CancelIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelInvitation(invitation);
                                }}
                                disabled={loading}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {invitations.map((invitation) => (
              <Card key={invitation.id} sx={{ mb: 2 }} variant="outlined">
                <CardContent>
                  <Typography variant="body1" fontWeight="medium" mb={2}>
                    {invitation.email}
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Role
                      </Typography>
                      <Box>
                        <Chip
                          label={getRoleLabel(invitation.role)}
                          color={getRoleColor(invitation.role) as any}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Invited
                      </Typography>
                      <Typography variant="body2">
                        {new Date(invitation.invitedAt).toLocaleDateString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Expires
                      </Typography>
                      <Typography variant="body2">
                        {new Date(invitation.expiresAt).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Send />}
                      onClick={() => handleResendInvitation(invitation)}
                      disabled={loading}
                      fullWidth
                    >
                      Resend
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => handleCancelInvitation(invitation)}
                      disabled={loading}
                      fullWidth
                    >
                      Cancel
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonAdd />Invite New User</Box></DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={1}>
            <TextField
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              fullWidth
              required
            />
            
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as any)}
                label="Role"
              >
                <MenuItem value="Organization_Member">Member</MenuItem>
                <MenuItem value="Organization_Admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info">
              The user will receive an email invitation with instructions to join your organization.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setInviteDialogOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInviteUser}
            disabled={loading || !inviteEmail.trim()}
            startIcon={<Send />}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Edit />Edit User Role</Box></DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Changing role for: <strong>{selectedMember?.email}</strong>
            </Typography>
            
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value as any)}
                label="Role"
              >
                <MenuItem value="Organization_Member">Member</MenuItem>
                <MenuItem value="Organization_Admin">Admin</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="warning">
              Role changes take effect immediately. Admins can manage users and organization settings.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditDialogOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditRole}
            disabled={loading}
            startIcon={<SaveIcon />}
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Remove Member Dialog */}
      <Dialog open={confirmRemoveDialogOpen} onClose={() => setConfirmRemoveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonRemoveIcon />Remove Member</Box></DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography>
              Are you sure you want to remove <strong>{selectedMember?.email}</strong> from the organization?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              To confirm, please type the user's email address below:
            </Typography>
            <TextField
              label="Confirm email address"
              value={deleteConfirmEmail}
              onChange={(e) => setDeleteConfirmEmail(e.target.value)}
              placeholder={selectedMember?.email}
              fullWidth
              error={deleteConfirmEmail !== '' && deleteConfirmEmail !== selectedMember?.email}
              helperText={deleteConfirmEmail !== '' && deleteConfirmEmail !== selectedMember?.email ? 'Email does not match' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirmRemoveDialogOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmRemoveMember}
            disabled={loading || deleteConfirmEmail !== selectedMember?.email}
            startIcon={<PersonRemoveIcon />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Cancel Invitation Dialog */}
      <Dialog open={confirmCancelDialogOpen} onClose={() => setConfirmCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><MailOutlineIcon />Cancel Invitation</Box></DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel the invitation for <strong>{selectedInvitation?.email}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirmCancelDialogOpen(false)} startIcon={<CancelIcon />}>
            No, Keep It
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmCancelInvitation}
            disabled={loading}
            startIcon={<CheckIcon />}
          >
            Yes, Cancel Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Ownership Confirmation Dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><TransferIcon />Transfer Ownership</Box></DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="warning">
              This action cannot be undone without the new owner's consent.
            </Alert>
            <Typography>
              You are about to transfer ownership of this organization to <strong>{transferMember?.email}</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              After the transfer:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li><Typography variant="body2"><strong>{transferMember?.email}</strong> will become the new Owner</Typography></li>
              <li><Typography variant="body2">You will be demoted to Admin</Typography></li>
              <li><Typography variant="body2">Only the new owner can transfer ownership back to you</Typography></li>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setTransferDialogOpen(false)} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleTransferOwnership}
            disabled={loading}
            startIcon={<TransferIcon />}
          >
            Transfer Ownership
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default UserManagement;
